import { PaginationType } from '../../types/utils/pagination.type';
import { EnclosureType } from '../../types/enclosure/enclosure.type';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { createSlice } from '@reduxjs/toolkit';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import snackbarUtils from '../../utils/SnackbarUtils';
import {
  deleteEnclosureDetails,
  getEnclosureDetails,
  getEnclosuresList,
  patchEnclosureDetails,
  postEnclosureDetails,
  postEnclosureOwnershipTransfer,
  postEnclosures,
} from './enclosures.action';

export interface EnclosuresState {
  enclosuresPostStatus: FETCH_STATUS;
  enclosuresBulk: null | Array<unknown>;
  enclosuresList: null | PaginationType<EnclosureType>;
  enclosuresListFetchStatus: FETCH_STATUS;
  enclosuresRouteParams: RouteParamsType;
  enclosureDetails: null | EnclosureType;
  enclosureDetailsFetchStatus: FETCH_STATUS;
  enclosureDetailsPostStatus: FETCH_STATUS;
  enclosureDetailsPatchStatus: FETCH_STATUS;
  enclosureDetailsDeleteStatus: FETCH_STATUS;
  enclosureOwnershipTransferStatus: FETCH_STATUS;
}

export const initialEnclosuresRouteParams = {
  page: 1,
  limit: 10,
  order: '-enclosure.id',
  q: '',
  in_service: 'all',
  country_id: '',
  country_category: '',
};

const INIT_STATE: EnclosuresState = {
  enclosuresPostStatus: FETCH_STATUS.NULL,
  enclosuresBulk: null,
  enclosuresRouteParams: initialEnclosuresRouteParams,
  enclosuresList: null,
  enclosuresListFetchStatus: FETCH_STATUS.NULL,
  enclosureDetails: null,
  enclosureDetailsFetchStatus: FETCH_STATUS.NULL,
  enclosureDetailsPostStatus: FETCH_STATUS.NULL,
  enclosureDetailsPatchStatus: FETCH_STATUS.NULL,
  enclosureDetailsDeleteStatus: FETCH_STATUS.NULL,
  enclosureOwnershipTransferStatus: FETCH_STATUS.NULL,
};

const enclosuresSlice = createSlice({
  name: 'enclosures',
  initialState: INIT_STATE,
  reducers: {
    setEnclosuresList: (state, action) => {
      state.enclosuresList = action.payload;
    },
    setEnclosuresListFetchStatus: (state, action) => {
      state.enclosuresListFetchStatus = action.payload;
    },
    setEnclosureDetails: (state, action) => {
      state.enclosureDetails = action.payload;
    },
    setEnclosureDetailsFetchStatus: (state, action) => {
      state.enclosureDetailsFetchStatus = action.payload;
    },
    setEnclosureDetailsPostStatus: (state, action) => {
      state.enclosureDetailsPostStatus = action.payload;
    },
    setEnclosureDetailsPatchStatus: (state, action) => {
      state.enclosureDetailsPatchStatus = action.payload;
    },
    setEnclosureDetailsDeleteStatus: (state, action) => {
      state.enclosureDetailsDeleteStatus = action.payload;
    },
    setEnclosuresPostStatus: (state, action) => {
      state.enclosuresPostStatus = action.payload;
    },
    setEnclosuresBulk: (state, action) => {
      state.enclosuresBulk = action.payload;
    },
    setEnclosureOwnershipTransferStatus: (state, action) => {
      state.enclosureOwnershipTransferStatus = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(SET_ROUTE_PARAMS, (state, action) => {
      const { payload } = action as ToolkitActionType<{ reducer: string; data: RouteParamsType }>;
      if (payload.reducer === 'enclosures') {
        state.enclosuresRouteParams = { ...state.enclosuresRouteParams, ...payload.data };
      }
    });

    // CREATE ENCLOSURES DETAILS
    builder.addCase(postEnclosureDetails.pending, state => {
      state.enclosureDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postEnclosureDetails.rejected, (state, action) => {
      state.enclosureDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postEnclosureDetails.fulfilled, state => {
      state.enclosureDetailsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Enclosure created successfully!');
      });
    });

    // CREATE ENCLOSURES DETAILS
    builder.addCase(postEnclosures.pending, state => {
      state.enclosuresPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postEnclosures.rejected, (state, action) => {
      state.enclosuresPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postEnclosures.fulfilled, state => {
      state.enclosuresPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Enclosures created successfully!');
      });
    });

    // GET ENCLOSURES LIST
    builder.addCase(getEnclosuresList.pending, state => {
      state.enclosuresListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getEnclosuresList.rejected, (state, action) => {
      if (action.payload) {
        state.enclosuresListFetchStatus = FETCH_STATUS.REJECTED;
        // state.enclosuresList = null;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getEnclosuresList.fulfilled, (state, action) => {
      state.enclosuresListFetchStatus = FETCH_STATUS.FULFILLED;
      state.enclosuresList = action.payload;
    });

    // GET ENCLOSURES DETAILS
    builder.addCase(getEnclosureDetails.pending, state => {
      state.enclosureDetailsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getEnclosureDetails.rejected, (state, action) => {
      state.enclosureDetailsFetchStatus = FETCH_STATUS.REJECTED;
      state.enclosureDetails = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getEnclosureDetails.fulfilled, (state, action) => {
      state.enclosureDetailsFetchStatus = FETCH_STATUS.FULFILLED;
      state.enclosureDetails = action.payload;
    });

    // PATCH ENCLOSURES DETAILS
    builder.addCase(patchEnclosureDetails.pending, state => {
      state.enclosureDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchEnclosureDetails.rejected, (state, action) => {
      state.enclosureDetailsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchEnclosureDetails.fulfilled, state => {
      state.enclosureDetailsPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Enclosure updated successfully!');
      });
    });

    // DELETE ENCLOSURES DETAILS
    builder.addCase(deleteEnclosureDetails.pending, state => {
      state.enclosureDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteEnclosureDetails.rejected, (state, action) => {
      state.enclosureDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteEnclosureDetails.fulfilled, state => {
      state.enclosureDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Enclosure deleted successfully!');
      });
    });

    // TRANSFER ENCLOSURE OWNERSHIP
    builder
      .addCase(postEnclosureOwnershipTransfer.pending, state => {
        state.enclosureOwnershipTransferStatus = FETCH_STATUS.PENDING;
      })
      .addCase(postEnclosureOwnershipTransfer.rejected, (state, action) => {
        state.enclosureOwnershipTransferStatus = FETCH_STATUS.REJECTED;
        setTimeout(() => snackbarUtils.error((action.payload as string) ?? 'Failed to transfer enclosure ownership'));
      })
      .addCase(postEnclosureOwnershipTransfer.fulfilled, state => {
        state.enclosureOwnershipTransferStatus = FETCH_STATUS.FULFILLED;
        setTimeout(() => snackbarUtils.success('Enclosure ownership transferred successfully!'));
      });
  },
});

export const {
  setEnclosureDetailsDeleteStatus,
  setEnclosureDetailsFetchStatus,
  setEnclosureDetailsPatchStatus,
  setEnclosureDetailsPostStatus,
  setEnclosuresListFetchStatus,
  setEnclosureDetails,
  setEnclosuresList,
  setEnclosuresPostStatus,
  setEnclosuresBulk,
  setEnclosureOwnershipTransferStatus,
} = enclosuresSlice.actions;

export default enclosuresSlice.reducer;
